import { UntilDestroy } from "@ngneat/until-destroy";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from "@angular/core";
import { CashplusOptInFacade, OptInViewModelInterface } from "./cashplus-optin.facade";
import { config } from "@config";
import { TranslationService } from "@core-services/translation.service";
import { Observable, take, tap } from "rxjs";
import { OptInTermsSlider } from "@modules/cashplus/features/opt-in/opt-in-terms-slider";
import {
	DYNAMIC_TERMS_TOKEN,
	TermsAndConditionsUpdatesComponent
} from "@modules/shared/terms-and-conditions/components/terms-and-conditions-updates.component";
import { AgreementBundle, UserJourney } from "@modules/shared/terms-and-conditions/entities/terms-and-conditions.model";
import { TermsAndConditionsService } from "@modules/shared/terms-and-conditions/services/terms-and-conditions.service";
import { UserService } from "@main-services/user.service";

@UntilDestroy()
@Component({
	selector: "cashplus-optin",
	templateUrl: "./cashplus-optin.component.html",
	styleUrls: ["./cashplus-optin.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [CashplusOptInFacade],
	standalone: false
})
export class CashPlusOptInComponent implements OnInit {
	@ViewChild(DYNAMIC_TERMS_TOKEN) dynamic_terms: TermsAndConditionsUpdatesComponent;
	public optInViewModel$: Observable<OptInViewModelInterface> = this.optInFacade.optInViewModel$();
	public optInTermsSlider: OptInTermsSlider = new OptInTermsSlider();
	public link =
		config.baseUrl + TranslationService.getLanguageIsoCode() + config.externalPaths["cashPlus_doc"]["download"];
	public isGerman: boolean = this.optInFacade.isGerman();

	public bundles: AgreementBundle[];
	public dynamictermAndConditionActive = false;

	public isDynamictermAndConditionActive$ = this.termsAndConditionsService.isDynamictermAndConditionActive$.pipe(
		tap((isActive) => {
			this.dynamictermAndConditionActive = isActive;
		})
	);

	private userJourneys: UserJourney[] = [];

	constructor(
		private optInFacade: CashplusOptInFacade,
		private readonly termsAndConditionsService: TermsAndConditionsService,
		private readonly cdr: ChangeDetectorRef,
		private readonly userService: UserService
	) {}

	ngOnInit(): void {
		this.userJourneys = [UserJourney.CashPlus];
		if (this.userService.user.needsRiskToggle()) {
			this.userJourneys.push(UserJourney.AppropriatenessWarning);
		}
		this.termsAndConditionsService
			.getAgreementBundleData$(this.userJourneys)
			.pipe(take(1))
			.subscribe((bundles) => {
				this.bundles = bundles;
				this.cdr.markForCheck();
			});
	}

	public get dynamicTermsInvalid(): boolean {
		return this.bundles.length > 0 && !this.dynamic_terms?.termsAndConditionsForm?.valid;
	}

	public onButtonClick(cardEligibility: boolean, nonAppropriateUser: boolean, isLimitOrdersEnabled: boolean): void {
		if (this.checkTermsAccepted(cardEligibility, nonAppropriateUser, isLimitOrdersEnabled)) {
			if (this.dynamictermAndConditionActive && this.bundles.length > 0) {
				this.termsAndConditionsService.acceptTerms(
					this.userJourneys,
					this.dynamic_terms?.termsAndConditionsForm?.value,
					() => {
						this.optInFacade.onOptInRequest();
					}
				);
			} else {
				this.optInFacade.onOptInRequest();
			}
		}
	}

	public isButtonDisabled(vm: OptInViewModelInterface): boolean {
		if (this.dynamictermAndConditionActive) {
			return this.bundles === undefined || (this.bundles.length > 0 && this.dynamicTermsInvalid);
		}
		return (
			!this.optInTermsSlider.card_terms ||
			(vm.isUserNotAppropriate && !this.optInTermsSlider.non_appropriate_user) ||
			(vm.isLimitOrdersEnabled && !this.optInTermsSlider.limit_orders) ||
			!this.optInTermsSlider.terms_and_conditions
		);
	}

	public checkTermsAccepted(
		userOwnCard: boolean,
		nonAppropriateUser: boolean,
		isLimitOrdersEnabled: boolean
	): boolean {
		if (this.dynamictermAndConditionActive) {
			if (this.bundles.length > 0 && this.dynamicTermsInvalid) {
				this.dynamic_terms.wiggleIt();
				return false;
			}
			return true;
		}

		let shouldWiggleCard = false;
		let shouldWiggleTermsConditions = false;
		let shouldWiggleNonAppropriateUser = false;
		let shouldWiggleLimitOrders = false;

		if (userOwnCard && !this.optInTermsSlider.card_terms) {
			shouldWiggleCard = true;
		}

		if (!this.optInTermsSlider.terms_and_conditions) {
			shouldWiggleTermsConditions = true;
		}

		if (nonAppropriateUser && !this.optInTermsSlider.non_appropriate_user) {
			shouldWiggleNonAppropriateUser = true;
		}

		if (isLimitOrdersEnabled && !this.optInTermsSlider.limit_orders) {
			shouldWiggleLimitOrders = true;
		}

		if (shouldWiggleCard) {
			this.optInTermsSlider.wiggleCard();
		}

		if (shouldWiggleTermsConditions) {
			this.optInTermsSlider.wiggleTermsConditions();
		}

		if (shouldWiggleNonAppropriateUser) {
			this.optInTermsSlider.nonAppropriateUser();
		}

		if (shouldWiggleLimitOrders) {
			this.optInTermsSlider.wiggleLimitOrders();
		}

		return (
			!shouldWiggleCard &&
			!shouldWiggleTermsConditions &&
			!shouldWiggleNonAppropriateUser &&
			!shouldWiggleLimitOrders
		);
	}
}
